/**
 * Inputs: Open-Meteo hourly payload + civil date. Outputs: a labelled now or midday line.
 * Noon is not presented as current weather at dinner.
 */

import { DateTime } from "luxon";
import { weatherPhrase } from "./labels.ts";
import type { LiveObservation } from "./schema.ts";

export type WeatherLine = {
  text: string;
  observedAt: string | null;
  stale: boolean;
};

type Hourly = {
  time?: string[];
  temperature_2m?: number[];
  precipitation_probability?: number[];
  weather_code?: number[];
};

export function weatherLine(
  obs: LiveObservation | null | undefined,
  localDate: string,
  now = new Date(),
  zone?: string,
  useCurrentHour = true,
): WeatherLine | null {
  if (!obs || obs.status === "unavailable" || obs.status === "not_yet_in_range") {
    return null;
  }
  const hourly = (obs.payload as { hourly?: Hourly } | undefined)?.hourly;
  if (!hourly?.time?.length) return null;
  const dayIndexes = hourly.time
    .map((stamp, index) => ({ stamp, index, hour: Number(stamp.slice(11, 13)) }))
    .filter((row) => row.stamp.startsWith(localDate) && Number.isFinite(row.hour));
  if (!dayIndexes.length) return null;
  const localNow = zone ? DateTime.fromJSDate(now, { zone }) : DateTime.fromJSDate(now);
  const today = useCurrentHour && localNow.toISODate() === localDate;
  const noon = dayIndexes.find((row) => row.hour === 12);
  const closest = dayIndexes.reduce((best, row) =>
    Math.abs(row.hour - localNow.hour) < Math.abs(best.hour - localNow.hour) ? row : best,
  );
  const pick = today ? closest : noon ?? closest;
  const temp = hourly.temperature_2m?.[pick.index];
  const rain = hourly.precipitation_probability?.[pick.index];
  const phrase = weatherPhrase(hourly.weather_code?.[pick.index]);
  if (temp === undefined && rain === undefined && !phrase) return null;
  const hourLabel = pick.stamp.slice(11, 16);
  const when = today ? `Around ${hourLabel}` : pick.hour === 12 ? "Around midday" : `Around ${hourLabel}`;
  return {
    text: [when, phrase, temp !== undefined ? `${Math.round(temp)}°` : null, rain !== undefined ? `${rain}% chance of rain` : null]
      .filter(Boolean)
      .join(" · "),
    observedAt: obs.observedAt ?? obs.fetchedAt,
    stale: obs.status === "stale",
  };
}
